import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

function getContractorsMeUrl() {
  const host = window.location.hostname;
  const isLocal = host === "localhost" || host === "127.0.0.1";

  // Dev: CRA proxy
  if (isLocal) return "/api/contractors/me";

  // Prod: Worker
  return "https://api.buildbright.app/contractors/proxy/me";
}

export default function ContractorSuccess() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);

  const sid = params.get("sid");
  const mode = params.get("mode") || "live";

  const [status, setStatus] = useState("checking"); // checking | authed | not_authed

  // Save sid so signup/onboarding can use it
  useEffect(() => {
    if (sid) {
      localStorage.setItem("bb_checkout_sid", sid);
      localStorage.setItem("bb_checkout_mode", mode);
    }
  }, [sid, mode]);

  // If already logged in, send straight to portal
  useEffect(() => {
    let cancelled = false;

    async function checkAuth() {
      setStatus("checking");
      try {
        const res = await fetch(getContractorsMeUrl(), {
          method: "GET",
          credentials: "include",
          headers: { accept: "application/json" },
        });

        if (cancelled) return;

        if (res.ok) {
          setStatus("authed");
          setTimeout(() => navigate("/contractors/portal"), 650);
        } else {
          setStatus("not_authed");
        }
      } catch {
        if (!cancelled) setStatus("not_authed");
      }
    }

    checkAuth();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const goSignup = () => {
    // Your App.js route is /contractors/signup (plural)
    const qs = sid ? `?sid=${encodeURIComponent(sid)}&mode=${encodeURIComponent(mode)}` : "";
    navigate(`/contractors/signup${qs}`);
  };

  const goLogin = () => {
    const qs = sid ? `?sid=${encodeURIComponent(sid)}&mode=${encodeURIComponent(mode)}` : "";
    navigate(`/contractors/login${qs}`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black px-4">
      <div className="w-full max-w-lg rounded-2xl border border-zinc-800 bg-black p-6 shadow-sm">
        <h1 className="text-2xl font-bold text-white">Payment successful ✅</h1>
        <p className="mt-2 text-zinc-200">
          Your checkout was completed. Next step: finish setting up your contractor account.
        </p>

        <div className="mt-3 text-sm text-zinc-300">
          Status:{" "}
          <span className="font-semibold">
            {status === "checking"
              ? "Checking login…"
              : status === "authed"
              ? "Logged in — sending you to Portal…"
              : "Not logged in"}
          </span>
        </div>

        {sid ? (
          <p className="mt-3 text-xs text-zinc-400 break-all">Session ID: {sid}</p>
        ) : (
          <p className="mt-3 text-xs text-orange-600">
            Missing sid. Stripe normally sends <b>?sid=CHECKOUT_SESSION_ID</b>.
          </p>
        )}

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          {/* Primary: Continue onboarding */}
          <button
            onClick={goSignup}
            className="rounded-full bg-orange-600 px-5 py-2 text-sm font-semibold text-white hover:bg-orange-700"
          >
            Continue onboarding
          </button>

          {/* Secondary: Login */}
          <button
            onClick={goLogin}
            className="rounded-full border border-zinc-700 px-5 py-2 text-sm font-semibold text-white hover:bg-black"
          >
            I already have an account (Login)
          </button>

          {/* Tertiary: Back */}
          <button
            onClick={() => navigate("/contractors/pricing")}
            className="rounded-full border border-zinc-700 px-5 py-2 text-sm font-semibold text-white hover:bg-black"
          >
            Back to Pricing
          </button>
        </div>

        <p className="mt-4 text-xs text-zinc-400">
          (Saved in browser as <b>bb_checkout_sid</b> so signup can attach your payment.)
        </p>
      </div>
    </div>
  );
}

